import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { identifyUser, posthog, trackEvent } from '@/lib/posthog';
import {
  FLOW_VERSION,
  STEP_KEYS,
  getFlowSessionId,
  hasFlowStarted,
  markFlowStarted,
  markStepStarted,
  resolveOnboardingFieldName,
  sanitizedErrorProperties,
  stepElapsedMs,
} from './onboardingFlow';

const VALIDATION_MESSAGES: Array<{ text: string; field: string }> = [
  { text: 'Please sign in to continue.', field: 'authentication' },
  { text: 'Please sign in.', field: 'authentication' },
  { text: 'Full name is required.', field: 'full_name' },
  { text: 'Full name is missing.', field: 'full_name' },
  { text: 'Business name is required.', field: 'business_name' },
  { text: 'Business name is missing.', field: 'business_name' },
  { text: 'Business address is required.', field: 'business_address' },
  { text: 'Business address is missing.', field: 'business_address' },
  { text: 'Timezone is required.', field: 'timezone' },
  { text: 'Booking URL is required.', field: 'booking_url' },
  { text: 'Booking URL is missing.', field: 'booking_url' },
  { text: 'Add at least one service.', field: 'services' },
  { text: 'All services must be at least $5.', field: 'services' },
  { text: 'Enable at least one working day.', field: 'working_hours' },
  { text: 'A tagline / title is required.', field: 'title' },
  { text: 'Tagline / title is missing.', field: 'title' },
];

function clampStep(rawStep: string | null): number {
  const parsed = Number.parseInt(rawStep ?? '1', 10);
  if (!Number.isFinite(parsed)) return 1;
  return Math.max(1, Math.min(5, parsed));
}

function isFormControl(
  target: EventTarget | null,
): target is HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement {
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLSelectElement ||
    target instanceof HTMLTextAreaElement
  );
}

/** V2Btn signals unavailability via aria-disabled so pointer events still fire. */
function isButtonBlocked(button: HTMLButtonElement): boolean {
  return button.disabled || button.getAttribute('aria-disabled') === 'true';
}

export default function OnboardingTelemetry({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const step = useMemo(() => clampStep(searchParams.get('step')), [searchParams]);

  // Lazy init: a plain useRef(getFlowSessionId()) would re-evaluate its
  // argument every render and re-mint a flow id after launch clears it.
  const flowSessionIdRef = useRef<string | null>(null);
  if (flowSessionIdRef.current === null) {
    flowSessionIdRef.current = getFlowSessionId();
  }

  const flowStartedAtRef = useRef<number | null>(null);
  const currentStepRef = useRef(step);
  const currentUserIdRef = useRef(user?.id);
  const viewedInitialStepRef = useRef(false);
  const identifiedUserRef = useRef<string | null>(null);
  const focusedFieldsRef = useRef<Set<string>>(new Set());
  const changedFieldsRef = useRef<Set<string>>(new Set());
  const lastValidationRef = useRef<string | null>(null);

  useEffect(() => {
    currentUserIdRef.current = user?.id;
  }, [user]);

  function properties(extra?: Record<string, unknown>) {
    const currentStep = currentStepRef.current;

    return {
      flow_version: FLOW_VERSION,
      flow_session_id: flowSessionIdRef.current,
      user_id: currentUserIdRef.current,
      step_number: currentStep,
      step_key: STEP_KEYS[currentStep],
      ...extra,
    };
  }

  useEffect(() => {
    flowStartedAtRef.current ??= Date.now();

    try {
      posthog.startSessionRecording();
    } catch {
      // Analytics must never block onboarding.
    }

    // A remount with the started flag already set for this flow id is a
    // return visit (OAuth redirect, page refresh), not a new funnel entry.
    const resumed = hasFlowStarted();
    trackEvent(resumed ? 'onboarding_resumed' : 'onboarding_started', properties({
      entry_step_number: currentStepRef.current,
      entry_step_key: STEP_KEYS[currentStepRef.current],
    }));
    markFlowStarted();

    const handleFocus = (event: FocusEvent) => {
      if (!isFormControl(event.target)) return;

      const fieldName = resolveOnboardingFieldName(event.target);
      if (focusedFieldsRef.current.has(fieldName)) return;

      focusedFieldsRef.current.add(fieldName);
      trackEvent('onboarding_field_focused', properties({
        field_name: fieldName,
        step_elapsed_ms: stepElapsedMs(),
      }));
    };

    const handleInput = (event: Event) => {
      if (!isFormControl(event.target)) return;

      const fieldName = resolveOnboardingFieldName(event.target);
      if (changedFieldsRef.current.has(fieldName)) return;

      changedFieldsRef.current.add(fieldName);
      trackEvent('onboarding_field_changed', properties({
        field_name: fieldName,
        step_elapsed_ms: stepElapsedMs(),
      }));
    };

    const checkForValidationError = () => {
      const pageText = document.body.innerText;
      const match = VALIDATION_MESSAGES.find(({ text }) => pageText.includes(text));

      if (!match || lastValidationRef.current === match.text) return;
      lastValidationRef.current = match.text;

      trackEvent('onboarding_validation_failed', properties({
        validation_field: match.field,
        validation_code: 'required_or_invalid',
        fields_focused: Array.from(focusedFieldsRef.current),
        fields_changed: Array.from(changedFieldsRef.current),
      }));
    };

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const button = target.closest('button');
      if (!(button instanceof HTMLButtonElement)) return;

      const buttonText = button.textContent?.trim().toLowerCase() ?? '';
      if (!buttonText.includes('continue') && !buttonText.includes('launch')) return;
      // "Continue with Google" is the OAuth button, not step navigation.
      if (buttonText.includes('google')) return;

      const blocked = isButtonBlocked(button);
      trackEvent(blocked ? 'onboarding_continue_blocked' : 'onboarding_continue_clicked', properties({
        action: buttonText.includes('launch') ? 'launch' : 'continue',
        button_disabled: blocked,
        step_elapsed_ms: stepElapsedMs(),
        fields_focused: Array.from(focusedFieldsRef.current),
        fields_changed: Array.from(changedFieldsRef.current),
      }));

      if (!blocked) {
        window.setTimeout(checkForValidationError, 100);
      }
    };

    const handleWindowError = (event: ErrorEvent) => {
      trackEvent('onboarding_error', properties({
        error_source: 'window',
        ...sanitizedErrorProperties(event.error),
      }));
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      trackEvent('onboarding_error', properties({
        error_source: 'unhandled_rejection',
        ...sanitizedErrorProperties(event.reason),
      }));
    };

    const handlePageHide = () => {
      trackEvent('onboarding_page_exited', properties({
        step_elapsed_ms: stepElapsedMs(),
        total_elapsed_ms: Date.now() - (flowStartedAtRef.current ?? Date.now()),
        fields_focused: Array.from(focusedFieldsRef.current),
        fields_changed: Array.from(changedFieldsRef.current),
      }));
    };

    document.addEventListener('focusin', handleFocus, true);
    document.addEventListener('input', handleInput, true);
    document.addEventListener('pointerdown', handlePointerDown, true);
    window.addEventListener('error', handleWindowError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      document.removeEventListener('focusin', handleFocus, true);
      document.removeEventListener('input', handleInput, true);
      document.removeEventListener('pointerdown', handlePointerDown, true);
      window.removeEventListener('error', handleWindowError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('pagehide', handlePageHide);
    };
  // Mount once so listeners remain stable; refs hold current step and user values.
   
  }, []);

  useEffect(() => {
    // On sign-out (account switch), AuthContext resets PostHog; clear the local
    // dedupe so signing back into the same account re-identifies instead of
    // being skipped as already-identified.
    if (!user) {
      identifiedUserRef.current = null;
      return;
    }

    if (identifiedUserRef.current === user.id) return;

    identifyUser(user.id, {
      email: user.email,
      name: user.user_metadata?.full_name || user.user_metadata?.name,
      auth_method: user.app_metadata?.provider || 'email',
      auth_created_at: user.created_at,
      onboarding_state: 'authenticated',
    });
    identifiedUserRef.current = user.id;

    trackEvent('onboarding_identity_attached', properties({
      auth_method: user.app_metadata?.provider || 'email',
    }));
   
  }, [user]);

  useEffect(() => {
    if (!viewedInitialStepRef.current) {
      viewedInitialStepRef.current = true;
      currentStepRef.current = step;
      markStepStarted();

      trackEvent('onboarding_step_viewed', properties());
      return;
    }

    const previousStep = currentStepRef.current;
    if (previousStep === step) return;

    // Forward jumps of more than one step are draft resumes or manual URL
    // edits, not completions — the step_viewed below records the origin.
    if (step === previousStep + 1) {
      trackEvent('onboarding_step_completed', {
        flow_version: FLOW_VERSION,
        flow_session_id: flowSessionIdRef.current,
        user_id: currentUserIdRef.current,
        step_number: previousStep,
        step_key: STEP_KEYS[previousStep],
        step_duration_ms: stepElapsedMs(),
        fields_focused: Array.from(focusedFieldsRef.current),
        fields_changed: Array.from(changedFieldsRef.current),
      });
    } else if (step < previousStep) {
      trackEvent('onboarding_back_navigated', {
        flow_version: FLOW_VERSION,
        flow_session_id: flowSessionIdRef.current,
        user_id: currentUserIdRef.current,
        from_step_number: previousStep,
        from_step_key: STEP_KEYS[previousStep],
        to_step_number: step,
        to_step_key: STEP_KEYS[step],
      });
    }

    currentStepRef.current = step;
    markStepStarted();
    focusedFieldsRef.current = new Set();
    changedFieldsRef.current = new Set();
    lastValidationRef.current = null;

    trackEvent('onboarding_step_viewed', properties(
      step > previousStep + 1 ? { resumed_from_step_number: previousStep } : undefined,
    ));
   
  }, [step]);

  return children;
}
